import _ from 'lodash';
import { BoostRaterVersionResponse } from '../api/types';
import type { CoverageState, Prime100QuotePageState } from '../types';
import {
  GlobalPropertyKeys,
  Prime100CoverageGroups,
  Prime100Coverages,
} from '../types';
import { PRIME_100_COVERAGES } from '../api/constants';
import type { Prime100EditQuotePageState } from '../pages/edit-quote/types';
const BASE_STATES = ['NY', 'WA'];

const isRenewQuoteCreationState = (
  state: unknown
): state is { basePolicyId: string } =>
  typeof state === 'object' &&
  state != null &&
  'basePolicyId' in state &&
  typeof (state as { basePolicyId: unknown }).basePolicyId === 'string';

type LimitParams = {
  aggLimit: number;
  state?: string;
  defaultLimit?: number;
  limit?: number;
};

export const BOOST_V5_COVERAGES = [
  Prime100Coverages.SOCIAL_ENGINEERING,
  Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME,
  Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE,
  Prime100Coverages.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT,
  Prime100Coverages.EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME,
  Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_NON_IT,
  Prime100Coverages.BI_EXTRA_EXPENSE_V5,
  Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT_V5,
  Prime100Coverages.ADDITIONAL_NAMED_INSURED,
  Prime100Coverages.COMPUTER_FUNDS_TRANSFER_FRAUD,
  Prime100Coverages.SECURITY_BREACH_LIABILITY,
];

export const BOOST_V6_COVERAGES: Prime100Coverages[] = [
  Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY_V6,
  Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
  Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS,
  Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
  Prime100Coverages.INVOICE_MANIPULATION,
  Prime100Coverages.BLANKET_ADDITIONAL_INSURED,
  Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT,
  Prime100Coverages.COURT_ATTENDANCE_COSTS,
  Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
  Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE,
  Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT,
];

export const V6_TO_V5_COVERAGE_MAP: Partial<
  Record<Prime100Coverages, Prime100Coverages>
> = {
  [Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT]:
    Prime100Coverages.PUBLIC_RELATIONS_EXPENSE,
  [Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT]:
    Prime100Coverages.SECURITY_BREACH_LIABILITY,
};

export const V5_TO_V6_COVERAGE_MAP: Partial<
  Record<Prime100Coverages, Prime100Coverages>
> = {
  [Prime100Coverages.PUBLIC_RELATIONS_EXPENSE]:
    Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT,
  [Prime100Coverages.SECURITY_BREACH_LIABILITY]:
    Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
};

/** Coverages shown (but disabled) when eligibility API reports not available */
export const COVERAGES_SHOWN_WHEN_UNAVAILABLE: Prime100Coverages[] = [
  Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY,
  Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY_V6,
  Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
  Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
  Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT,
];

/** Independent mutual exclusion pairs – each pair is isolated, no cross-pair effects.
 * Only Enhanced BI sub-coverages (V5) are mutually exclusive; Contingent BI sub-coverages (V6) are not. */
export const MUTUAL_EXCLUSION_PAIRS: [
  Prime100Coverages,
  Prime100Coverages
][] = [
  [
    Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
    Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
  ],
  [
    Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME,
    Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE,
  ],
  [
    Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT,
    Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT,
  ],
];

export const OMITTED_P100_COVERAGES: Prime100Coverages[] = [
  Prime100Coverages.BUSINESS_INCOME_ECOMMERCE_AMENDMENT,
  Prime100Coverages.EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME,
  Prime100Coverages.FUNDS_TRANSFER_LIABILITY_LOSS_EXCLUSION,
  Prime100Coverages.THIRD_PARTY_PATENT_OR_TRADE_SECRET_INFO_CARVEBACK,
  Prime100Coverages.WAIVER_OF_SUBROGATION,
  Prime100Coverages.OTHER_INSURANCE_AMENDATORY_PRIMARY_AND_NON_CONTRIBUTORY,
];

export const MANDATORY_COVERAGE_ELIGIBILITY_API_KEYS: Partial<
  Record<Prime100Coverages, string>
> = {
  [Prime100Coverages.SECURITY_BREACH_EXPENSE]: 'securityBreachResponseExpense',
  [Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA]:
    'recoveryOrRestorationOfElectronicData',
  [Prime100Coverages.PUBLIC_RELATIONS_EXPENSE]: 'reputationalHarmSublimit',
  [Prime100Coverages.PCI_FINES_AND_PENALTIES]: 'pciFinesAndPenalties',
  [Prime100Coverages.REGULATOR_DEFENSE_AND_PENALTIES]:
    'regulatoryDefenseAndPenalties',
  [Prime100Coverages.COURT_ATTENDANCE_COSTS]: 'courtAttendanceCosts',
  [Prime100Coverages.SECURITY_BREACH_LIABILITY]:
    'fundsTransferLiabilitySublimit',
  [Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT]:
    'fundsTransferLiabilitySublimit',
};

export function isMandatoryCoverageAvailableFromApi(
  coverageId: Prime100Coverages,
  coverageValues: Record<string, unknown> | undefined
): boolean {
  const apiKey = MANDATORY_COVERAGE_ELIGIBILITY_API_KEYS[coverageId];
  if (!apiKey || !coverageValues) return false;
  const val = coverageValues[apiKey];
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false;
  return (val as { isAvailable?: boolean }).isAvailable !== false;
}

export const ELIGIBILITY_GLOBAL_KEYS = [
  'limit',
  'deductible',
  'waitingPeriod',
  'retroactivePeriod',
  'raterVersion',
];

export const ELIGIBILITY_HIDDEN_COVERAGES: Prime100Coverages[] = [
  Prime100Coverages.SOCIAL_ENGINEERING,
  Prime100Coverages.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT,
  Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_NON_IT,
];

export function shouldShowCoverageForVersion(
  coverageId: Prime100Coverages,
  version: BoostRaterVersionResponse
): boolean {
  if (
    version === BoostRaterVersionResponse.V6 &&
    BOOST_V5_COVERAGES.includes(coverageId)
  ) {
    return false;
  }
  if (
    version === BoostRaterVersionResponse.V5 &&
    BOOST_V6_COVERAGES.includes(coverageId)
  ) {
    return false;
  }
  return true;
}

export const prime100CoverageStateFactory = (): Prime100CoverageStateFactory =>
  prime100CoverageStateFactoryV5();

export const prime100CoverageStateFactoryV5 =
  (): Prime100CoverageStateFactory => {
    return {
      [Prime100CoverageGroups.FIRST_PARTY_COVERAGES]: [
        {
          id: Prime100Coverages.SECURITY_BREACH_EXPENSE,
          isMandatory: true,
          limit: (params: LimitParams) => params.aggLimit,
          isSelected: true,
        },
        {
          id: Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA,
          isMandatory: true,
          limit: (params: LimitParams) => params.aggLimit,
          isSelected: true,
        },
        {
          id: Prime100Coverages.PUBLIC_RELATIONS_EXPENSE,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => {
            if (!params.state) {
              throw new Error(
                `State needs to be passed for ${Prime100Coverages.PUBLIC_RELATIONS_EXPENSE} limit`
              );
            }
            return BASE_STATES.includes(params.state)
              ? 50_000 / params.aggLimit
              : params.aggLimit * 0.05;
          },
        },
        {
          id: Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT,
          isSelected: true,
          isMandatory: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          supplementalText: (boostRaterVersion) => {
            if (boostRaterVersion === BoostRaterVersionResponse.V4) {
              return 'Extortion Threat Sublimit: 5% of Aggregate Limit';
            }
            return '';
          },
        },
        {
          id: Prime100Coverages.BI_EXTRA_EXPENSE,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          subCoverages: [
            Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME,
            Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE,
          ],
        },
        {
          id: Prime100Coverages.COMPUTER_FUNDS_TRANSFER_FRAUD,
          isSelected: true,
          limit: (params: LimitParams) => params.aggLimit,
        },
        {
          id: Prime100Coverages.SOCIAL_ENGINEERING,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          hasModal: true,
          modalData: (state: State) => {
            return {
              accountId: state.account.id,
              isVerifyingBankAccounts:
                state.socialEngineering.isVerifyingBankAccounts,
              isAuthenticatingFundTransferRequests:
                state.socialEngineering.isAuthenticatingFundTransferRequests,
              isPreventingUnauthorizedWireTransfers:
                state.socialEngineering.isPreventingUnauthorizedWireTransfers,
              isEditOrVerify: Boolean(state.quoteDetails),
              quoteId: state.quoteDetails?.id,
              submissionId: state.submissionId,
            };
          },
          subCoverages: [
            Prime100Coverages.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT,
          ],
        },
      ],
      [Prime100CoverageGroups.THIRD_PARTY_COVERAGES]: [
        {
          id: Prime100Coverages.PCI_FINES_AND_PENALTIES,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.aggLimit,
        },
        {
          id: Prime100Coverages.REGULATOR_DEFENSE_AND_PENALTIES,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.aggLimit,
        },
        {
          id: Prime100Coverages.SECURITY_BREACH_LIABILITY,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
        {
          id: Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY,
          isSelected: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
      ],
      [Prime100CoverageGroups.ENDORSEMENTS]: [
        {
          id: Prime100Coverages.HARDWARE_REPLACEMENT_COSTS,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSelected: false,
        },
        {
          id: Prime100Coverages.TELECOM_FRAUD,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSelected: false,
        },
        {
          id: Prime100Coverages.POST_BREACH_REMEDIATION,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSelected: false,
        },
        {
          id: Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME,
          isSelected: false,
          hasModal: true,
          modalData: (state: State) => {
            return {
              accountId: state.account.id,
              backupFreequency: state.enhancedBusinessAndCbi?.backupFrequency,
              businessContinuityMeasures:
                state.enhancedBusinessAndCbi?.businessContinuityMeasures,
              isEditOrVerify:
                Boolean(state.quoteDetails) &&
                !isRenewQuoteCreationState(state),
              quoteId: state.quoteDetails?.id,
              isRenewal: state.quoteDetails?.isRenewal,
              submissionId: state.submissionId,
            };
          },
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE,
          isSelected: false,
          hasModal: true,
          modalData: (state: State) => {
            return {
              accountId: state.account.id,
              backupFreequency: state.enhancedBusinessAndCbi?.backupFrequency,
              businessContinuityMeasures:
                state.enhancedBusinessAndCbi?.businessContinuityMeasures,
              isEditOrVerify:
                Boolean(state.quoteDetails) &&
                !isRenewQuoteCreationState(state),
              quoteId: state.quoteDetails?.id,
              isRenewal: state.quoteDetails?.isRenewal,
              submissionId: state.submissionId,
            };
          },
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME,
          isSelected: false,
        },
        {
          id: Prime100Coverages.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSelected: false,
        },
        {
          id: Prime100Coverages.SERVICE_FRAUD_AND_CRYPTOJACKING,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSelected: false,
        },
        {
          id: Prime100Coverages.ADDITIONAL_INSURED,
          isSelected: false,
          hasModal: true,
          modalData: (state: State) =>
            state.coverageState?.ADDITIONAL_INSURED.modalData ?? [],
        },
      ],
      [Prime100CoverageGroups.TRIA]: [
        {
          id: Prime100Coverages.TRIA,
          isSelected: false,
        },
      ],
      [Prime100CoverageGroups.ADDITIONAL_NAMED_INSURED]: [
        {
          id: Prime100Coverages.ADDITIONAL_NAMED_INSURED,
          isSelected: false,
          hasModal: true,
          modalData: (state: State) => {
            if (
              state.coverageState?.ADDITIONAL_NAMED_INSURED?.modalData?.length
            ) {
              return state.coverageState.ADDITIONAL_NAMED_INSURED.modalData;
            }
            if (state.additionalNamedInsured) {
              return state.additionalNamedInsured;
            }
            return [];
          },
        },
      ],
    };
  };

const buildV6EndorsementState = () => {
  const defaultLimit = (params: LimitParams) => params.limit ?? params.defaultLimit;
  const entries: any[] = [];

  entries.push({ id: Prime100Coverages.HARDWARE_REPLACEMENT_COSTS, limit: defaultLimit, isSelected: true });
  entries.push({ id: Prime100Coverages.POST_BREACH_REMEDIATION, limit: defaultLimit, isSelected: true });
  entries.push({ id: Prime100Coverages.SERVICE_FRAUD_AND_CRYPTOJACKING, limit: defaultLimit, isSelected: false });
  entries.push({ id: Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT, limit: defaultLimit, isSelected: false });
  entries.push({ id: Prime100Coverages.TELECOM_FRAUD, limit: defaultLimit, isSelected: true });
  entries.push({ id: Prime100Coverages.BLANKET_ADDITIONAL_INSURED, isSelected: true });
  entries.push({ id: Prime100Coverages.ADDITIONAL_INSURED, isSelected: false, hasModal: true, modalData: (state: State) => state.coverageState?.ADDITIONAL_INSURED.modalData ?? [] });
  entries.push({ id: Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT, isSelected: false, limit: defaultLimit });

  return entries;
};

export const prime100CoverageStateFactoryV6 =
  (): Prime100CoverageStateFactory => {
    return {
      [Prime100CoverageGroups.FIRST_PARTY_COVERAGES]: [
        {
          id: Prime100Coverages.SECURITY_BREACH_EXPENSE,
          isMandatory: true,
          limit: (params: LimitParams) => params.aggLimit,
          isSelected: true,
        },
        {
          id: Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA,
          isMandatory: true,
          limit: (params: LimitParams) => params.aggLimit,
          isSelected: true,
        },
        {
          id: Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT,
          isMandatory: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSelected: true,
        },
        {
          id: Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT,
          isSelected: true,
          isMandatory: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
        {
          id: Prime100Coverages.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          subCoverages: [
            Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE,
            Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT,
          ],
        },
        {
          id: Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          subCoverages: [
            Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS,
          ],
        },
        {
          id: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
          isSubCoverage: true,
        },
        {
          id: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
        {
          id: Prime100Coverages.INVOICE_MANIPULATION,
          isSelected: false,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
      ],
      [Prime100CoverageGroups.THIRD_PARTY_COVERAGES]: [
        {
          id: Prime100Coverages.PCI_FINES_AND_PENALTIES,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.aggLimit,
        },
        {
          id: Prime100Coverages.REGULATOR_DEFENSE_AND_PENALTIES,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.aggLimit,
        },
        {
          id: Prime100Coverages.COURT_ATTENDANCE_COSTS,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) =>
            params.limit ?? params.defaultLimit ?? params.aggLimit,
        },
        {
          id: Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
          isMandatory: true,
          isSelected: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
        {
          id: Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY_V6,
          isSelected: true,
          limit: (params: LimitParams) => params.limit ?? params.defaultLimit,
        },
      ],
      [Prime100CoverageGroups.ENDORSEMENTS]: buildV6EndorsementState(),
      [Prime100CoverageGroups.ADDITIONAL_NAMED_INSURED]: [
        {
          id: Prime100Coverages.ADDITIONAL_NAMED_INSURED,
          isSelected: false,
          hasModal: true,
          modalData: (state: State) => {
            if (
              state.coverageState?.ADDITIONAL_NAMED_INSURED?.modalData?.length
            ) {
              return state.coverageState.ADDITIONAL_NAMED_INSURED.modalData;
            }
            if (state.additionalNamedInsured) {
              return state.additionalNamedInsured;
            }
            return [];
          },
        },
      ],
      [Prime100CoverageGroups.TRIA]: [
        {
          id: Prime100Coverages.TRIA,
          isSelected: true,
        },
      ],
    };
  };

type State = Prime100QuotePageState<
  Record<Prime100Coverages, CoverageState> | undefined
> &
  Partial<Prime100EditQuotePageState>;

type Prime100CoverageStateFactory = {
  [K in Prime100CoverageGroups]: Prime100CoverageState[];
};

type Prime100CoverageState = {
  id: Prime100Coverages;
  isMandatory?: boolean;
  limit?: (params: LimitParams) => number | undefined;
  hasModal?: boolean;
  modalData?: (state: State) => Record<string, any>;
  isSelected: boolean;
  supplementalText?: (boostRaterVersion: BoostRaterVersionResponse) => string;
  subCoverages?: Prime100Coverages[];
  isSubCoverage?: boolean;
};

/**
 * Returns a consolidated list of globalKeys and P100 Coverage keys.
 * @returns Array of union and de-duped list of P100 coverage keys and Global property keys(retroactivePeriod, deductible, waitingPeriod and limit)
 */
export const getConsolidatedP100CoverageKeys = (): string[] => {
  /*
   * used Set to de-dupe the list of keys.
   * list of keys combined from following two sources. Global Property keys and PRIME_100_COVERAGES keys
   */
  const set = new Set<string>();
  Object.values(GlobalPropertyKeys).forEach((_key: any) => set.add(_key));
  PRIME_100_COVERAGES.forEach((_coverage) =>
    Object.entries(_coverage).forEach((_entry: any) => {
      if (_entry[0] === 'name' || _.isEmpty(_entry[1])) {
        return;
      }

      set.add(_entry[1]);
    })
  );
  return Array.from(set);
};

/**
 * Override coverage names based on boost version.
 * Use when the P100/state API returns a single name but V5/V6 require different labels.
 */
export const COVERAGE_NAME_OVERRIDES_BY_VERSION: Partial<
  Record<BoostRaterVersionResponse, Partial<Record<Prime100Coverages, string>>>
> = {
  [BoostRaterVersionResponse.V6]: {
    [Prime100Coverages.SECURITY_BREACH_EXPENSE]:
      'Security Breach Response Expense',
    [Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA]:
      'Recovery / Restoration of Electronic Data',
    [Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT]:
      'Public Relations / Reputational Harm',
    [Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT_V5]:
      'Extortion Threats / Ransom Payments',
    [Prime100Coverages.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT]:
      'Business Income & Extra Expense (Security Failure & System Failure)',
    [Prime100Coverages.SECURITY_BREACH_LIABILITY]:
      'Security Breach Liability / Funds Transfer Fraud Liability',
    [Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT]:
      'Security Breach Liability / Funds Transfer Fraud Liability',
    [Prime100Coverages.HARDWARE_REPLACEMENT_COSTS]:
      'Hardware Replacement and Bricking Costs',
    [Prime100Coverages.TELECOM_FRAUD]: 'Telecommunications Fraud',
    [Prime100Coverages.POST_BREACH_REMEDIATION]: 'Post Breach Remediation',
    [Prime100Coverages.BLANKET_ADDITIONAL_INSURED]:
      'Blanket Additional Insured',
    [Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT]:
      'Funds Transfer Fraud & Social Engineering (Dual Verification Required)',
    [Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING]:
      'Funds Transfer Fraud & Social Engineering',
    [Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS]:
      'Enhancement to Include Client Held Funds',
  },
};

export function getCoverageNameForVersion(
  coverageId: Prime100Coverages,
  boostVersion: BoostRaterVersionResponse | undefined,
  defaultName: string
): string {
  if (!boostVersion) return defaultName;
  const overrides = COVERAGE_NAME_OVERRIDES_BY_VERSION[boostVersion];
  return overrides?.[coverageId] ?? defaultName;
}

export const getAdditionalNamedInsuredCoverage = () => ({
  [Prime100Coverages.ADDITIONAL_NAMED_INSURED]: {
    name: 'Additional Named Insured',
    tooltip:
      'Additional Named Insureds must be represented in your application for insurance.',
    errorMessage: '',
    ordinal: 27,
  },
});
